import { RequestCooldownService } from './request-cooldown.service';
import { SettingsService as SettingsApi } from '../../shared/api/generated/settings/settings.service';
import { SetupService as SetupApi } from '../../shared/api/generated/setup/setup.service';
import type { SetAppConfigBody } from '../../shared/api/generated/models';
import { AppSettingsSchema, SetupResponseSchema } from '@andor/contracts';
import type { SetupRoomInput, SetupResponse } from '@andor/contracts';
export type { SetupResponse } from '@andor/contracts';
import { inject, Injectable, signal } from '@angular/core';
import { distinctUntilChanged, switchMap, tap } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { AppConfigStore } from '../../state/app-config.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { debug } from '@4players/odin-common';
import { AppConfig } from '../../shared/schemas/settings';
import { Title } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { config } from '../../../environments/environment';
import { RoleWithPassword } from '../../shared/schemas/backend-api';
import { AppStore } from '../../state/app.repository';
import { derivePrimaryVivid, derivePrivateColor } from '../../shared/helpers/color';
import { configDefault } from '../../shared/constants/app-config-default';

export type ErrorResponse = {
  error: true;
  message: string;
};

/**
 * A room defined locally during onboarding, sent with the one-time setup call.
 * The client chooses the `roomId` so it can prove the e2e cipher password for
 * it (`passwordProof`); `tempId` correlates the created room back to its local
 * entry. The password itself is never sent.
 */
export type SetupRoomPayload = SetupRoomInput;
export type SetupCreatedRoom = SetupResponse['rooms'][number];

/**
 * Talks to the app-server about the app configuration (`/v1/settings`,
 * `/v1/setup`, realtime updates) and applies the configured colours to the
 * document. The draft/saved/pending-file state itself lives in
 * {@link AppConfigStore}.
 */
@Injectable({
  providedIn: 'root',
})
export class AppConfigService {
  private readonly cooldown = inject(RequestCooldownService);
  #appConfigStore = inject(AppConfigStore);
  #userSettingsStore = inject(UserSettingsStore);
  #titleService = inject(Title);
  #settingsApi = inject(SettingsApi);
  #setupApi = inject(SetupApi);
  #appStore = inject(AppStore);
  configSyncState = signal<{}>(false);

  constructor() {
    this.handleAppStyles();
    // The tab title follows the draft like the theme does, so editing previews
    // the name and Cancel, Save and realtime updates all restore it.
    this.#appConfigStore.appTitle$
      .pipe(distinctUntilChanged())
      .subscribe((title) => this.#titleService.setTitle(title.trim() || configDefault.title));
  }
  handleAppStyles() {
    this.#appConfigStore.appStyles$
      .pipe(
        switchMap((entities) => {
          return this.#userSettingsStore.colorMode$.pipe(
            tap((mode) => {
              if (mode === null) return;
              debug(`updating app style entities for '${mode}' mode`, { ...entities });
              const root = document.documentElement;
              entities.forEach((entity) => {
                root.style.setProperty(entity.id, entity[mode]);
                // Override Angular Material color variables
                if (entity.id === '--accent-color') {
                  root.style.setProperty('--sys-primary', `rgb(${entity[mode]})`);
                }
              });
              // Derive the private-message color from the (user-configurable)
              // accent + primary so it harmonizes with any theme yet stays
              // distinct from the accent. Recomputed here on every style/mode
              // change; falls back to the static `variables.scss` value when
              // the accent can't be parsed.
              const accent = entities.find((e) => e.id === '--accent-color')?.[mode];
              const primary = entities.find((e) => e.id === '--primary-color')?.[mode];
              const privateColor = derivePrivateColor(accent, primary);
              if (privateColor) {
                root.style.setProperty('--private-color', privateColor);
              }
              // Readable stroke/glow version of the primary (see
              // `derivePrimaryVivid`); consumers fall back to the accent.
              const primaryVivid = derivePrimaryVivid(primary, accent, mode);
              if (primaryVivid) {
                root.style.setProperty('--primary-vivid', primaryVivid);
              }
            })
          );
        })
      )
      .subscribe(() => {});
  }

  /**
   * Realtime settings update from the server. While the user has unsaved
   * edits, only the saved baseline moves (so Cancel lands on the newest server
   * state); the draft is left alone. Otherwise the update is applied directly.
   */
  handleSettingsUpdate(update: AppConfig) {
    if (this.#appConfigStore.hasConfigChanged()) {
      this.#appConfigStore.setSavedState(update);
      return;
    }
    this.#appConfigStore.commit(update);
  }

  /**
   * Persists the draft config together with any pending image files in a
   * single multipart request. On success the store is committed to the
   * canonical settings echoed by the server (real asset URLs); on failure the
   * draft and pending files are kept so the user can fix and retry.
   */
  async syncConfig(): Promise<AppConfig | ErrorResponse> {
    const formData = this.#buildConfigFormData();

    try {
      const updatedSettings = await firstValueFrom(this.#settingsApi.setAppConfig(formData));
      if (updatedSettings) {
        this.#appConfigStore.commit(AppSettingsSchema.parse(updatedSettings));
      }
      return AppSettingsSchema.parse(updatedSettings);
    } catch (error: unknown) {
      return {
        error: true,
        message: this.#httpErrorMessage(error, 'Error saving settings. Please try again later.'),
      };
    }
  }

  /**
   * One-shot initial app setup: uploads any selected assets and persists the
   * app config + roles + the owner's initial `rooms` in a single multipart
   * request to `/v1/setup`. Mirrors {@link syncConfig} but also carries the
   * role/permission payload and the room list, and posts to the unauthenticated,
   * one-time setup endpoint.
   *
   * Returns the created rooms (each with its server-minted `roomId`) so the
   * caller can correlate them with the local entries — e.g. to persist e2e
   * cipher passwords under the real ids and build invite links.
   */
  async syncSetup(
    // Password optional: the default role (guest) is sent without one.
    permissions: Record<string, RoleWithPassword>,
    rooms: SetupRoomPayload[]
  ): Promise<SetupResponse | ErrorResponse> {
    const formData = this.#buildConfigFormData();

    try {
      const response = await firstValueFrom(
        this.#setupApi.appSetup({
          ...formData,
          permissions: JSON.stringify(permissions),
          rooms: JSON.stringify(rooms),
        })
      );
      return SetupResponseSchema.parse(response);
    } catch (error: unknown) {
      this.cooldown.capture('setup', error);
      return {
        error: true,
        message: this.#httpErrorMessage(error, 'Error setting up the app. Please try again later.'),
      };
    }
  }

  /**
   * Builds the multipart body shared by the settings-update and initial-setup
   * endpoints: a base64-stripped `config` JSON plus the paired `images` /
   * `config-keys` arrays for every pending file.
   *
   * The data: URL is stripped on a *copy* of each asset entity, so the live
   * elf store is never mutated — the server fills in the real URL after the
   * upload and echoes the canonical settings back.
   */
  #buildConfigFormData(): SetAppConfigBody {
    const value = this.#appConfigStore.value;
    const files = this.#appConfigStore.pendingFiles();
    const configToSend = {
      ...value,
      // The draft keeps what the user typed; surrounding spaces are not part of the name.
      title: value.title.trim(),
      assets: Object.fromEntries(
        Object.entries(value.assets).map(([key, asset]) => [
          key,
          { ...asset, ...(files.has(key) && { url: '' }) },
        ])
      ),
    };

    return {
      config: JSON.stringify(AppSettingsSchema.parse(configToSend)),
      images: [...files.values()],
      'config-keys': [...files.keys()],
    };
  }

  #httpErrorMessage(error: unknown, fallback: string): string {
    if (
      error instanceof HttpErrorResponse &&
      error.error?.message &&
      typeof error.error.message === 'string'
    ) {
      return error.error.message;
    }
    return fallback;
  }

  closeSettings() {
    this.#appStore.update({ panelSettingsExpanded: false });
  }
}
